<template>
    <div class="q-pa-md">
        <q-table
            ref="viewportTable"
            :rows="team"
            :columns="columns"
            row-key="id"
            :loading="loadingTeam"
            :filter="filter"
            v-model:pagination="pagination"
            :rows-per-page-options="rowsPerPageOptions"
            flat
            bordered
        >
            <template v-slot:top-right>
                <q-input dense debounce="300" v-model="filter" placeholder="Search">
                    <template v-slot:append>
                        <q-icon name="search" />
                    </template>
                </q-input>
            </template>

            <template v-slot:body-cell-role="props">
                <q-td :props="props">
                    <q-badge :color="props.row.role === 'admin' ? 'deep-purple' : 'teal'" outline>
                        {{ roleLabel(props.row.role) }}
                    </q-badge>
                </q-td>
            </template>

            <template v-slot:body-cell-is_active="props">
                <q-td :props="props">
                    <q-toggle
                        v-if="canSetActiveness"
                        :model-value="props.row.is_active"
                        :label="props.row.is_active ? 'Active' : 'Inactive'"
                        color="positive"
                        :disable="!canManage(props.row) || savingStatusIds.has(props.row.id)"
                        @update:model-value="isActive => setActiveness(props.row, isActive)"
                    >
                        <q-tooltip v-if="!canManage(props.row)">
                            You can only change staff below your own level.
                        </q-tooltip>
                    </q-toggle>
                    <q-badge
                        v-else
                        :color="props.row.is_active ? 'positive' : 'grey-7'"
                        outline
                    >
                        {{ props.row.is_active ? "Active" : "Inactive" }}
                    </q-badge>
                </q-td>
            </template>

            <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                    <q-btn
                        outline
                        no-caps
                        size="sm"
                        color="primary"
                        icon="o_tune"
                        label="Manage permissions"
                        :disable="!canManage(props.row)"
                        @click="openPermissions(props.row)"
                    >
                        <q-tooltip v-if="!canManage(props.row)">
                            You can only manage staff below your own level.
                        </q-tooltip>
                    </q-btn>
                </q-td>
            </template>

            <template v-slot:no-data>
                <div class="full-width row flex-center q-pa-md text-grey">
                    No team members to manage.
                </div>
            </template>
        </q-table>

        <q-dialog v-model="dialogOpen">
            <q-card style="min-width: 420px; max-width: 560px;">
                <q-card-section class="row items-center q-pb-none">
                    <div class="text-h6">{{ selectedStaffName }}</div>
                    <q-space />
                    <q-btn icon="close" flat round dense v-close-popup />
                </q-card-section>

                <q-card-section class="text-caption text-grey-8">
                    Toggling a flag sets a per-user override. Flags you do not hold
                    yourself are disabled.
                </q-card-section>

                <q-card-section v-if="ownPermissionsError" class="text-caption text-negative q-pt-none">
                    Your own permissions couldn't be loaded, so some toggles are
                    disabled regardless of what you hold. Reload the page to try again.
                </q-card-section>

                <q-separator />

                <q-card-section>
                    <q-inner-loading :showing="loadingPermissions" />

                    <q-list v-if="!loadingPermissions" separator>
                        <q-item v-for="row in permissionRows" :key="row.key">
                            <q-item-section>
                                <q-item-label>{{ row.label }}</q-item-label>
                                <q-item-label caption>{{ row.description }}</q-item-label>
                                <q-item-label caption v-if="row.override !== null" class="text-primary">
                                    Overridden &middot; role default is
                                    {{ row.roleDefault ? "on" : "off" }}
                                </q-item-label>
                            </q-item-section>

                            <q-item-section side>
                                <div class="row items-center no-wrap">
                                    <q-btn
                                        v-if="row.override !== null"
                                        flat
                                        dense
                                        size="sm"
                                        no-caps
                                        color="grey-7"
                                        label="Reset"
                                        :disable="!canChangePermission(row) || savingKey !== null"
                                        @click="resetPermission(row)"
                                    />
                                    <q-toggle
                                        :model-value="row.effective"
                                        color="primary"
                                        :disable="!canChangePermission(row) || savingKey !== null"
                                        @update:model-value="value => savePermission(row, value)"
                                    />
                                </div>
                            </q-item-section>
                        </q-item>
                    </q-list>
                </q-card-section>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import viewportTablePagination from "@/mixins/viewportTablePagination";
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import token from "@/services/util/roleHandler";
import { roleLabel } from "@/services/util/roles";

const ROLE_HIERARCHY = ["client", "basic", "team_leader", "admin", "developer"];

export default {
    name: "StaffPermissionsPanel",
    mixins: [viewportTablePagination],

    data() {
        return {
            role: token.getRole(),
            team: [],
            loadingTeam: false,
            filter: "",
            savingStatusIds: new Set(),
            ownPermissions: {},
            ownPermissionsError: false,
            dialogOpen: false,
            selectedStaff: null,
            permissionRows: [],
            loadingPermissions: false,
            savingKey: null,
            columns: [
                {
                    name: "name",
                    label: "Name",
                    align: "left",
                    field: (row) => `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim(),
                    sortable: true,
                },
                { name: "email", label: "Email", align: "left", field: "email", sortable: true },
                { name: "role", label: "Role", align: "left", field: "role", sortable: true },
                { name: "is_active", label: "Status", align: "left", field: "is_active", sortable: true },
                { name: "actions", label: "", align: "right", field: "actions" },
            ],
        };
    },

    computed: {
        canSetActiveness() {
            return this.role === "admin" || this.role === "developer";
        },
        selectedStaffName() {
            if (!this.selectedStaff) return "";
            return `${this.selectedStaff.first_name ?? ""} ${this.selectedStaff.last_name ?? ""}`.trim();
        },
    },

    created() {
        this.fetchOwnPermissions();
        this.fetchTeam();
    },

    methods: {
        roleLabel,

        canChangePermission(row) {
            const adminManagedKeys = ["create_case_notes", "edit_case_notes", "edit_whaiora_status"];
            return !!this.ownPermissions[row.key]
                && (!adminManagedKeys.includes(row.key) || ["admin", "developer"].includes(this.role));
        },

        canManage(staff) {
            const ownLevel = ROLE_HIERARCHY.indexOf(this.role);
            const staffLevel = ROLE_HIERARCHY.indexOf(staff.role);
            return ownLevel !== -1 && staffLevel !== -1 && ownLevel > staffLevel;
        },

        async fetchTeam() {
            this.loadingTeam = true;
            try {
                const response = await api.users.getMyTeam();
                this.team = response.data ?? [];
            } catch (error) {
                console.error("Error loading team:", error);
                notify.error("Could not load your team.", "top");
            } finally {
                this.loadingTeam = false;
            }
        },

        async fetchOwnPermissions() {
            try {
                const response = await api.users.getPermissionCatalogue();
                this.ownPermissions = response.data?.own ?? {};
                this.ownPermissionsError = false;
            } catch (error) {
                console.error("Error loading permission catalogue:", error);
                this.ownPermissionsError = true;
                notify.error("Could not load your permissions; some controls may be disabled. Please reload.", "top");
            }
        },

        async setActiveness(staff, isActive) {
            if (this.savingStatusIds.has(staff.id)) return;
            this.savingStatusIds.add(staff.id);
            try {
                await api.users.updateStaffActiveness(staff.id, isActive);
                this.team = this.team.map((member) =>
                    member.id === staff.id ? { ...member, is_active: isActive } : member
                );
                notify.primary(`Staff member marked ${isActive ? "active" : "inactive"}.`, "top");
            } catch (error) {
                console.error("Error updating staff activeness:", error);
                const message = error?.response?.data?.error || error?.response?.data;
                notify.error(typeof message === "string" ? message : "Could not update staff status.", "top");
            } finally {
                this.savingStatusIds.delete(staff.id);
            }
        },

        async openPermissions(staff) {
            this.selectedStaff = staff;
            this.permissionRows = [];
            this.dialogOpen = true;
            this.loadingPermissions = true;
            try {
                const response = await api.users.getStaffPermissions(staff.id);
                this.permissionRows = response.data ?? [];
            } catch (error) {
                console.error("Error loading permissions:", error);
                notify.error("Could not load this staff member's permissions.", "top");
                this.dialogOpen = false;
            } finally {
                this.loadingPermissions = false;
            }
        },

        async savePermission(row, allowed) {
            await this.applyPermissionChange(row, { key: row.key, allowed });
        },

        async resetPermission(row) {
            await this.applyPermissionChange(row, { key: row.key, allowed: null });
        },

        async applyPermissionChange(row, payload) {
            if (!this.canChangePermission(row)) return;
            if (this.savingKey !== null) return;
            this.savingKey = row.key;
            try {
                const response = await api.users.updateStaffPermissions(this.selectedStaff.id, payload);
                this.permissionRows = response.data ?? this.permissionRows;
                notify.primary("Permission updated.", "top");
            } catch (error) {
                console.error("Error updating permission:", error);
                const message = error?.response?.data;
                notify.error(typeof message === "string" ? message : "Could not update permission.", "top");
            } finally {
                this.savingKey = null;
            }
        },
    },
};
</script>
